import { documentTypeOf, normalizeDocument } from '@/lib/document'

export const EMPTY = '—'

export function initials(name?: string | null): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = parts[0][0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : ''
  return (first + last).toUpperCase()
}

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })

export function money(value?: number | string | null): string {
  if (value === null || value === undefined || value === '') return EMPTY
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? BRL.format(parsed) : EMPTY
}

const DATE = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
const DATE_TIME = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })

const asDate = (value?: string | number | Date | null): Date | null => {
  if (value === null || value === undefined || value === '') return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export function formatDate(value?: string | number | Date | null): string {
  const date = asDate(value)
  return date ? DATE.format(date) : EMPTY
}

const pad2 = (value: number) => String(value).padStart(2, '0')

export function toPlainDate(date: Date = new Date()): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
}

export function formatPlainDate(value?: string | null): string {
  return formatDate(value ? `${value.slice(0, 10)}T00:00:00` : null)
}

const LONG_DATE = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })

export function formatLongPlainDate(value?: string | Date | null): string | null {
  if (!value) return null
  const plain = value instanceof Date ? toPlainDate(value) : value.slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(plain)) return null
  const [year, month, day] = plain.split('-').map(Number)
  return LONG_DATE.format(new Date(year, month - 1, day))
}

export function formatDateTime(value?: string | number | Date | null): string {
  const date = asDate(value)
  return date ? DATE_TIME.format(date).replace(',', '') : EMPTY
}

export function formatDocument(value?: string | null): string {
  const document = normalizeDocument(value)
  const type = documentTypeOf(document)
  if (type === 'cpf') return `${document.slice(0, 3)}.${document.slice(3, 6)}.${document.slice(6, 9)}-${document.slice(9)}`
  if (type === 'cnpj') return `${document.slice(0, 2)}.${document.slice(2, 5)}.${document.slice(5, 8)}/${document.slice(8, 12)}-${document.slice(12)}`
  return value ?? ''
}

export function plural(count: number, one: string, many: string): string {
  return `${count.toLocaleString('pt-BR')} ${count === 1 ? one : many}`
}

export function optionalNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export function maskBrDate(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8)
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}
